import { useState, useEffect } from 'react'
import { X, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import eventService from '../../services/eventService'
import coachService from '../../services/coachService'

export default function CreateCoachEventModal({ isOpen, onClose, onCreated, editSeries = null }) {
  const defaultForm = {
    title: '',
    description: '',
    recurrenceRule: 'NONE',
    recurrenceStart: '',
    recurrenceEnd: '',
    startMinute: 540,
    durationMinutes: 60,
    maxCapacity: '',
    waitlistEnabled: false,
    meetingLink: '',
    linkAvailableMinutesBefore: null,
    visibleTo: 'COACH_CLIENTS'
  }

  const getInitialForm = () => {
    if (!editSeries) return defaultForm
    const startDate = editSeries.recurrenceStart ? new Date(editSeries.recurrenceStart) : null
    const endDate = editSeries.recurrenceEnd ? new Date(editSeries.recurrenceEnd) : null
    return {
      ...defaultForm,
      title: editSeries.title || '',
      description: editSeries.description || '',
      recurrenceRule: editSeries.recurrenceRule || 'NONE',
      recurrenceStart: startDate ? startDate.toISOString().split('T')[0] : '',
      recurrenceEnd: endDate ? endDate.toISOString().split('T')[0] : '',
      startMinute: editSeries.startMinute ?? 540,
      durationMinutes: editSeries.durationMinutes ?? 60,
      maxCapacity: editSeries.maxCapacity ?? '',
      waitlistEnabled: editSeries.waitlistEnabled ?? false,
      meetingLink: editSeries.meetingLink || '',
      linkAvailableMinutesBefore: editSeries.linkAvailableMinutesBefore ?? null,
      visibleTo: editSeries.visibleTo || 'COACH_CLIENTS'
    }
  }

  const [form, setForm] = useState(getInitialForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [clients, setClients] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [selectedClientIds, setSelectedClientIds] = useState(new Set())

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialForm())
      // Pre-select targets if editing
      if (editSeries?.targetUsers) {
        setSelectedClientIds(new Set(editSeries.targetUsers.map((t) => t.userId)))
      } else {
        setSelectedClientIds(new Set())
      }
    }
  }, [isOpen, editSeries])

  // Load clients when "Specific Clients" is selected
  useEffect(() => {
    if (isOpen && form.visibleTo === 'SPECIFIC_USERS' && clients.length === 0) {
      setClientsLoading(true)
      coachService
        .getClients()
        .then((res) => setClients(res.data?.clients || res.clients || []))
        .catch(() => toast.error('Failed to load clients'))
        .finally(() => setClientsLoading(false))
    }
  }, [isOpen, form.visibleTo])

  const toggleClient = (clientId) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev)
      if (next.has(clientId)) next.delete(clientId)
      else next.add(clientId)
      return next
    })
  }

  if (!isOpen) return null

  const isEditing = !!editSeries

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return toast.error('Title is required')
    if (!isEditing && !form.recurrenceStart) return toast.error('Start date is required')
    if (form.visibleTo === 'SPECIFIC_USERS' && selectedClientIds.size === 0)
      return toast.error('Select at least one client')

    setIsSubmitting(true)
    try {
      // Combine date + time into proper local datetime
      const h = Math.floor(parseInt(form.startMinute, 10) / 60)
      const min = parseInt(form.startMinute, 10) % 60
      const buildDateTimeISO = (dateStr) => {
        if (!dateStr) return null
        const [y, m, d] = dateStr.split('-').map(Number)
        return new Date(y, m - 1, d, h, min, 0, 0).toISOString()
      }
      const buildDateNoonISO = (dateStr) => {
        if (!dateStr) return null
        const [y, m, d] = dateStr.split('-').map(Number)
        return new Date(y, m - 1, d, 12, 0, 0).toISOString()
      }
      const data = {
        ...form,
        recurrenceStart: form.recurrenceStart ? buildDateTimeISO(form.recurrenceStart) : undefined,
        recurrenceEnd: form.recurrenceEnd ? buildDateNoonISO(form.recurrenceEnd) : null,
        startMinute: parseInt(form.startMinute, 10),
        durationMinutes: parseInt(form.durationMinutes, 10),
        maxCapacity: form.maxCapacity ? parseInt(form.maxCapacity, 10) : null,
        reminderConfig: [{ minutesBefore: 1440 }, { minutesBefore: 60 }],
        targetUserIds: form.visibleTo === 'SPECIFIC_USERS' ? [...selectedClientIds] : []
      }
      if (isEditing) {
        await eventService.updateCoachEvent(editSeries.id, data)
        toast.success('Event updated')
      } else {
        await eventService.createCoachEvent(data)
        toast.success('Event created')
      }
      onCreated?.()
      onClose()
    } catch (error) {
      toast.error(
        error.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} event`
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }))

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isEditing ? 'Edit Coaching Event' : 'Create Coaching Event'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={200}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              placeholder="e.g., SAT Study Group"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Supports **bold**, *italic*, [links](url)"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">Supports Markdown formatting</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recurrence
              </label>
              <select
                value={form.recurrenceRule}
                onChange={(e) => set('recurrenceRule', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              >
                <option value="NONE">One-time</option>
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="BIWEEKLY">Biweekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Visible To
              </label>
              <select
                value={form.visibleTo}
                onChange={(e) => set('visibleTo', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              >
                <option value="COACH_CLIENTS">All My Clients</option>
                <option value="SPECIFIC_USERS">Specific Clients</option>
              </select>
            </div>
          </div>

          {/* Client Selector (when Specific Clients) */}
          {form.visibleTo === 'SPECIFIC_USERS' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Clients{' '}
                {selectedClientIds.size > 0 && `(${selectedClientIds.size} selected)`}
              </label>
              {clientsLoading ? (
                <p className="text-sm text-gray-500 py-2">Loading clients...</p>
              ) : clients.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">No clients found</p>
              ) : (
                <div className="border dark:border-gray-600 rounded-lg max-h-40 overflow-y-auto">
                  {clients.map((client) => {
                    const c = client.client || client
                    const isSelected = selectedClientIds.has(c.id)
                    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleClient(c.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b last:border-b-0 dark:border-gray-700 ${isSelected ? 'bg-violet-50 dark:bg-violet-900/20' : ''}`}
                      >
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-violet-600 border-violet-600' : 'border-gray-300 dark:border-gray-500'}`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="truncate text-gray-900 dark:text-gray-100">{name}</span>
                        <span className="text-xs text-gray-400 ml-auto truncate">{c.email}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date {!isEditing && '*'}
              </label>
              <input
                type="date"
                value={form.recurrenceStart}
                onChange={(e) => set('recurrenceStart', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              />
            </div>
            {form.recurrenceRule !== 'NONE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={form.recurrenceEnd}
                  onChange={(e) => set('recurrenceEnd', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={`${String(Math.floor(form.startMinute / 60)).padStart(2, '0')}:${String(form.startMinute % 60).padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number)
                  set('startMinute', h * 60 + m)
                }}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (min)
              </label>
              <input
                type="number"
                value={form.durationMinutes}
                onChange={(e) => set('durationMinutes', e.target.value)}
                min={15}
                max={480}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Capacity
              </label>
              <input
                type="number"
                value={form.maxCapacity}
                onChange={(e) => set('maxCapacity', e.target.value)}
                min={1}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.waitlistEnabled}
                  onChange={(e) => set('waitlistEnabled', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Enable waitlist</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Meeting Link
            </label>
            <input
              type="url"
              value={form.meetingLink}
              onChange={(e) => set('meetingLink', e.target.value)}
              placeholder="https://zoom.us/j/..."
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
            />
          </div>

          {form.meetingLink && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Link Available
              </label>
              <select
                value={form.linkAvailableMinutesBefore ?? ''}
                onChange={(e) =>
                  set(
                    'linkAvailableMinutesBefore',
                    e.target.value === '' ? null : parseInt(e.target.value, 10)
                  )
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
              >
                <option value="">Always visible</option>
                <option value="0">When event starts</option>
                <option value="15">15 min before</option>
                <option value="30">30 min before</option>
                <option value="60">1 hour before</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {isSubmitting
                ? isEditing
                  ? 'Saving...'
                  : 'Creating...'
                : isEditing
                  ? 'Save Changes'
                  : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
